import {
  normalizeCanvasStrokeWidth,
  type CanvasDrawingNodeProps,
} from "@/pages/canvas/types/canvasNodeData";
import { AnimatePresence } from "motion/react";
import { memo, useCallback, useEffect, useState } from "react";
import { useCanvasApi } from "../context/CanvasContext";
import {
  useCanvasSelectionCount,
  useIsMultiSelectToolbarOpen,
} from "../context/CanvasSelectionCountContext";
import { drawingStrokePath, readDrawingPoints } from "../utils/drawingStroke";
import { drawingTipInk, normalizeDrawingTip } from "../utils/drawingTips";
import {
  DrawingFloatingToolbar,
  type DrawingStyle,
} from "./DrawingFloatingToolbar";
import { DEFAULT_EDGE_COLOR } from "./shared/CanvasColorPalettes";
import { CanvasFloatingToolbarPortal } from "./shared/CanvasFloatingToolbarPortal";

/** Extra hit width (flow px) either side of the ink, so thin strokes are clickable. */
const HIT_PADDING = 12;

/** Width of the selection halo drawn behind the ink. */
const SELECTION_HALO = 6;

export const DrawingNode = memo(function DrawingNode({
  id,
  data,
  selected,
  dragging,
  width = 0,
  height = 0,
  positionAbsoluteX,
  positionAbsoluteY,
}: CanvasDrawingNodeProps) {
  const canvas = useCanvasApi();
  const { canEdit } = canvas;

  const path = drawingStrokePath(readDrawingPoints(data.points));
  const color = data.color ?? DEFAULT_EDGE_COLOR;
  const strokeWidth = normalizeCanvasStrokeWidth(data.stroke_width);
  const ink = drawingTipInk(normalizeDrawingTip(data.tip), strokeWidth);

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const handlePopoverOpenChange = useCallback((open: boolean) => {
    setIsPopoverOpen(open);
  }, []);

  const handleStyleChange = useCallback(
    (style: Partial<DrawingStyle>) => {
      canvas.updateNodeData(id, style as Record<string, unknown>);
    },
    [id, canvas],
  );

  const selectedNodeCount = useCanvasSelectionCount();
  const isMultiSelectToolbarOpen = useIsMultiSelectToolbarOpen();

  // Multi-select unmounts the toolbar without a Radix onOpenChange(false);
  // clear the latch on deselect so it can't keep itself mounted.
  useEffect(() => {
    if (!isPopoverOpen) return;
    if (isMultiSelectToolbarOpen || !selected) {
      handlePopoverOpenChange(false);
    }
  }, [
    isMultiSelectToolbarOpen,
    selected,
    isPopoverOpen,
    handlePopoverOpenChange,
  ]);

  const showToolbar =
    canEdit &&
    !dragging &&
    !isMultiSelectToolbarOpen &&
    selected &&
    selectedNodeCount === 1;

  return (
    <div
      style={{ width, height, position: "relative", pointerEvents: "none" }}
      className="overflow-visible"
    >
      <AnimatePresence>
        {showToolbar && (
          <CanvasFloatingToolbarPortal
            positionAbsoluteX={positionAbsoluteX}
            positionAbsoluteY={positionAbsoluteY}
            width={width}
            topOffset={64}
          >
            <DrawingFloatingToolbar
              style={{ color, stroke_width: strokeWidth }}
              onStyleChange={handleStyleChange}
              onPopoverOpenChange={handlePopoverOpenChange}
            />
          </CanvasFloatingToolbarPortal>
        )}
      </AnimatePresence>

      <svg
        width={width}
        height={height}
        style={{
          position: "absolute",
          inset: 0,
          overflow: "visible",
          pointerEvents: "none",
        }}
      >
        {/* Wide transparent copy — only the ink itself is grabbable, so the
            empty corners of the padded box stay click-through. */}
        <path
          d={path}
          stroke="transparent"
          strokeWidth={ink.strokeWidth + HIT_PADDING * 2}
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
          style={{ pointerEvents: "stroke", cursor: "pointer" }}
        />

        {selected && (
          <path
            d={path}
            stroke="var(--canvas-selection)"
            strokeWidth={ink.strokeWidth + SELECTION_HALO}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
            opacity={0.35}
          />
        )}

        <path
          d={path}
          stroke={color}
          strokeWidth={ink.strokeWidth}
          strokeLinecap={ink.linecap}
          strokeLinejoin="round"
          fill="none"
          opacity={ink.opacity}
        />
      </svg>
    </div>
  );
});
